import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spark } from "../components/Spark";
import { SITE_NAME } from "../constants/site";
import { Check, X, Crown, Sparkles, Loader2, ArrowRight } from "lucide-react";

// Fallback quando o gateway não está configurado (o backend devolve 503)
const UPGRADE_CONTACT_URL = "https://wa.me/5531990682809?text=";

const ORDER = ["gratis", "medio", "pro"];

// Fallback caso a API demore/caia (o backend free do Render dorme) — a porta
// de entrada nunca pode ficar presa num spinner. Mesmo shape da landing.
const FALLBACK_PLANS = {
  precos: { gratis: 0, medio: 19.9, pro: 39.9 },
  nomes: { gratis: "Grátis", medio: "Médio", pro: "Pro" },
  tabela: [
    { recurso: "Chat tira-dúvidas", gratis: "15 msg/dia", medio: "Ilimitado", pro: "Ilimitado" },
    { recurso: "Modo Prova (foco no conteúdo)", gratis: false, medio: true, pro: true },
    { recurso: "Flashcards + revisão espaçada", gratis: "Básico", medio: true, pro: true },
    { recurso: "Quiz / Simulado", gratis: "3/dia", medio: "Ilimitado", pro: "Ilimitado + cronometrado" },
    { recurso: "Upload de material (PDF/foto)", gratis: false, medio: true, pro: true },
    { recurso: "Mapa mental interativo", gratis: false, medio: false, pro: true },
  ],
};

export default function Planos({ publico = false }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [uso, setUso] = useState(null);

  useEffect(() => {
    api.get("/plans", { silent: true }).then((r) => setPlans(r.data)).catch(() => setPlans(FALLBACK_PLANS));
    if (!publico) api.get("/me/uso", { silent: true }).then((r) => setUso(r.data)).catch(() => {});
  }, [publico]);

  if (!plans) {
    const spinner = (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
    return publico ? <PublicShell>{spinner}</PublicShell> : spinner;
  }

  const planoAtual = user?.plano || "gratis";
  const limiteChat = uso?.limites?.chat_dia;
  const limiteQuiz = uso?.limites?.quiz_dia;

  const conteudo = (
    <div data-testid="planos-page" className="space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {publico ? "Escolha como quer começar" : "Planos"}
        </h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          O Grátis já te ajuda de verdade. O Médio tira os limites. O Pro libera tudo.
        </p>
      </div>

      {/* Contadores do dia (só quando há limite) */}
      {uso && (limiteChat != null || limiteQuiz != null) && (
        <div data-testid="uso-hoje" className="mx-auto max-w-md rounded-xl border border-border bg-card p-3 text-center text-xs text-muted-foreground">
          Hoje: <strong className="text-foreground">{uso.uso_hoje?.chat ?? 0}/{limiteChat}</strong> mensagens
          {" · "}
          <strong className="text-foreground">{uso.uso_hoje?.quiz ?? 0}/{limiteQuiz}</strong> quizzes
          <span className="block mt-0.5">Reseta à meia-noite.</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3 max-w-4xl mx-auto">
        {ORDER.map((p) => (
          <PlanCard
            key={p}
            plano={p}
            nome={plans.nomes?.[p] || p}
            preco={plans.precos?.[p] ?? 0}
            tabela={plans.tabela || []}
            atual={!publico && planoAtual === p}
            popular={p === "medio"}
            publico={publico}
          />
        ))}
      </div>

      <p className="text-center text-[11px] text-muted-foreground max-w-md mx-auto">
        Pagamento seguro via Mercado Pago (assinatura mensal, cancela quando quiser).
        Seu plano é liberado na hora, automaticamente, assim que o pagamento é aprovado.
      </p>

      {publico && (
        <p className="text-center text-xs text-muted-foreground">
          Já tem conta?{" "}
          <Link data-testid="planos-login-link" to="/login" className="text-violet-400 hover:text-violet-300 font-medium">
            Entrar
          </Link>
        </p>
      )}
    </div>
  );

  return publico ? <PublicShell>{conteudo}</PublicShell> : conteudo;
}

// Casca da versão pública (deslogado) — mesma paleta sólida do app.
function PublicShell({ children }) {
  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <Spark className="h-8 w-8 shrink-0" />
            <span className="font-bold tracking-tight whitespace-nowrap text-sm sm:text-base">{SITE_NAME}</span>
          </Link>
          <Link
            to="/login"
            data-testid="planos-nav-login"
            className="rounded-full px-3 sm:px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 whitespace-nowrap"
          >
            Entrar
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">{children}</main>
    </div>
  );
}

function PlanCard({ plano, nome, preco, tabela, atual, popular, publico = false }) {
  const [busy, setBusy] = useState(false);
  const [payErr, setPayErr] = useState(null);
  const destaque = popular ? "border-violet-500/60 shadow-lg shadow-violet-500/10" : "border-border";

  const assinar = async () => {
    setBusy(true); setPayErr(null);
    try {
      const { data } = await api.post("/payments/subscribe", { plano });
      window.location.href = data.init_point; // checkout do Mercado Pago
    } catch (e) {
      setPayErr(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid={`plan-card-${plano}`} className={`relative rounded-2xl border bg-card card-depth p-5 flex flex-col gap-4 ${destaque}`}>
      {popular && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-violet-600 text-white text-[10px] font-semibold uppercase tracking-wide px-3 py-0.5">
          Mais popular
        </span>
      )}
      <div className="text-center space-y-1 pt-1">
        <p className="flex items-center justify-center gap-1.5 font-bold tracking-tight">
          {plano === "pro" ? <Crown className="h-4 w-4 text-amber-400" /> : plano === "medio" ? <Sparkles className="h-4 w-4 text-violet-400" /> : null}
          {nome}
        </p>
        <p className="text-2xl font-bold">
          {preco > 0 ? (
            <>R$ {preco.toFixed(2).replace(".", ",")}<span className="text-xs font-normal text-muted-foreground">/mês</span></>
          ) : (
            <>R$ 0</>
          )}
        </p>
      </div>

      <ul className="space-y-2 text-xs flex-1">
        {tabela.map((row) => (
          <li key={row.recurso} className="flex items-start gap-2">
            <CellIcon value={row[plano]} />
            <span className="text-muted-foreground">
              <span className="text-foreground/90">{row.recurso}</span>
              {typeof row[plano] === "string" && <span className="block text-[11px]">{row[plano]}</span>}
            </span>
          </li>
        ))}
      </ul>

      {publico ? (
        // Porta de entrada: escolher leva pro cadastro carregando o plano.
        // O Grátis tem CTA claro — a opção gratuita nunca fica escondida.
        <Link
          to={`/cadastro?plano=${plano}`}
          data-testid={`plan-choose-${plano}`}
          className={`w-full rounded-full text-center py-2.5 text-sm font-medium transition inline-flex items-center justify-center gap-2 ${
            plano === "gratis" || popular
              ? "bg-violet-600 hover:bg-violet-500 text-white glow-violet"
              : "border border-border hover:border-violet-500/50 text-foreground/90"
          }`}
        >
          {plano === "gratis" ? "Começar grátis" : `Escolher o ${nome}`} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ) : atual ? (
        <div data-testid={`plan-current-${plano}`} className="rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 text-center py-2.5 text-sm font-medium">
          Seu plano atual
        </div>
      ) : plano === "gratis" ? (
        <div className="rounded-full border border-border text-muted-foreground text-center py-2.5 text-sm">
          Plano de entrada
        </div>
      ) : (
        <div className="space-y-2">
          <button
            data-testid={`plan-upgrade-${plano}`}
            onClick={assinar}
            disabled={busy}
            className="w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white text-center py-2.5 text-sm font-medium transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? "Abrindo o checkout..." : `Assinar o ${nome}`}
          </button>
          {payErr && (
            <p className="text-[11px] text-amber-400 text-center">
              {payErr}{" "}
              <a
                className="underline underline-offset-2"
                href={`${UPGRADE_CONTACT_URL}${encodeURIComponent(`Quero o plano ${nome} do Sagaz`)}`}
                target="_blank"
                rel="noreferrer"
              >Falar com o suporte</a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CellIcon({ value }) {
  if (value === false) return <X className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0" />;
  return <Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />;
}
